import { IconButton } from "@package/subframe/components/IconButton";
import { Tabs } from "@package/subframe/components/Tabs";
import {
  FeatherAlertCircle,
  FeatherHistory,
  FeatherMoreVertical,
  FeatherPenTool,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { DeleteEventRecordDialog } from "@/components/events-hub/delete-event-record-dialog";
import { EventsHubFilters } from "@/components/events-hub/events-hub-filters";
import { EventsHubTable } from "@/components/events-hub/events-hub-table";
import { CountBadge } from "@/components/ui/count-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import {
  useAcknowledgeEventRecordCommandControllerAcknowledge,
  useTransitionEventRecordCommandControllerArchive,
  useTransitionEventRecordCommandControllerReopen,
  useTransitionEventRecordCommandControllerResolve,
  useTransitionEventRecordCommandControllerRestore,
} from "@/generated/event-records/event-records";
import { useListEventTypesQueryControllerList } from "@/generated/event-types/event-types";
import type { EventRecordListItemDto } from "@/generated/model/eventRecordListItemDto";
import { useEventsHubData } from "@/hooks/use-events-hub-data";
import { invalidateAcknowledgementQueries } from "@/lib/acknowledgements/invalidate-acknowledgement-queries";
import type { SortDir } from "@/lib/category-display";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import { NO_EVENT_RECORD_CAPABILITIES } from "@/lib/event-records/capabilities";
import { invalidateEventRecordQueries } from "@/lib/event-records/invalidate-event-record-queries";
import {
  DEFAULT_SORT,
  DEFAULT_TAB,
  EMPTY_FILTERS,
  EMPTY_MESSAGE,
  type EventsHubSortColumn,
  type EventsHubTab,
  hasActiveFilters,
  itemNounFor,
  PAGE_SIZE,
} from "@/lib/events-hub";

const routeApi = getRouteApi("/_authenticated/event-records/");

/**
 * The Events Hub (NAU-341): every event record for the selected nursery across
 * three tabs, with the lifecycle actions on each row.
 *
 * Deliberately not wrapped in a FeatureGate. Monitoring is Boost-gated but
 * recording — and therefore reviewing — a fire drill is a legal requirement, so a
 * Launch-plan manager must be able to see and acknowledge what their staff filed.
 *
 * Every row action is gated on `record.capabilities` — resolved server-side from
 * the nursery's own acknowledgement rules and the record's current status
 * (NAU-342). The page keeps no role predicates and no status preconditions of its
 * own, so a nursery that changes who may acknowledge or resolve changes this menu
 * without a deploy, and the menu cannot drift from what the API will allow.
 */
export function EventsHubPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tab: tabParam } = routeApi.useSearch();
  const tab: EventsHubTab = tabParam ?? DEFAULT_TAB;

  const { selectedNurseryId, nurseries, setSelectedNursery } = useNursery();

  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [eventTypeId, setEventTypeId] = useState<string | undefined>();
  const [sortColumn, setSortColumn] = useState<
    EventsHubSortColumn | undefined
  >();
  const [sortDir, setSortDir] = useState<SortDir | undefined>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [pendingDelete, setPendingDelete] =
    useState<EventRecordListItemDto | null>(null);

  // Reset paging and sort during render rather than in an effect: back/forward
  // navigation changes the tab without remounting, so an effect would repaint the
  // old page first. Same guard the Monitoring page uses.
  const [lastTab, setLastTab] = useState(tab);
  if (lastTab !== tab) {
    setLastTab(tab);
    setPage(0);
    setSortColumn(undefined);
    setSortDir(undefined);
  }

  const effectiveSort = {
    column: sortColumn ?? DEFAULT_SORT[tab].column,
    dir: sortDir ?? DEFAULT_SORT[tab].dir,
  };

  // Resolving or archiving the last row on a page empties it while rows remain
  // earlier in the list. The pagination footer only renders alongside rows, so
  // without this the user is stranded on an empty page reading "no events" with no
  // control to get back. Unique to this hub: it is the first whose row actions
  // remove rows from the tab being viewed.
  const [clampedTotal, setClampedTotal] = useState(0);

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const allCategories = categoriesResponse?.data.items ?? [];

  const { data: eventTypesResponse } = useListEventTypesQueryControllerList(
    selectedNurseryId ?? "",
    { query: { enabled: !!selectedNurseryId } },
  );
  const eventTypes = eventTypesResponse?.data?.items ?? [];

  const { items, total, isLoading, isError, counts } = useEventsHubData({
    nurseryId: selectedNurseryId ?? undefined,
    tab,
    categoryId,
    eventTypeId,
    sortBy: effectiveSort.column,
    sortDir: effectiveSort.dir,
    page,
    pageSize,
  });

  if (total !== clampedTotal) {
    setClampedTotal(total);
    const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (total > 0 && page > lastPage) {
      setPage(lastPage);
    }
  }

  const acknowledge = useAcknowledgeEventRecordCommandControllerAcknowledge();
  const resolve = useTransitionEventRecordCommandControllerResolve();
  const archive = useTransitionEventRecordCommandControllerArchive();
  const restore = useTransitionEventRecordCommandControllerRestore();
  const reopen = useTransitionEventRecordCommandControllerReopen();

  const setTab = (next: EventsHubTab) => {
    navigate({ to: "/event-records", search: { tab: next } });
  };

  const handleSort = (column: EventsHubSortColumn) => {
    if (column === effectiveSort.column) {
      setSortDir(effectiveSort.dir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  /**
   * Every lifecycle action shares this shape, and each shows the server's own
   * message on failure — a 409 explains exactly why the move was refused
   * ("already resolved", "not archived"), which is more useful than a generic line.
   *
   * Invalidating rather than refetching is load-bearing: an action moves the record
   * to a DIFFERENT tab, and each tab is its own query key. `refetch()` would only
   * update the tab in view, leaving the destination tab — and the record's detail
   * page, and the dashboard's Events tab — serving pre-action cache for the app's
   * five-minute staleTime.
   */
  /**
   * The acknowledgement helper rather than the event-record one, because
   * Acknowledge is one of the actions routed through here and it signs a request
   * in the shared acknowledgement context, not just the record. Without this the
   * record's tracking panel still shows that person as outstanding when you open
   * it, for the app's five-minute staleTime.
   *
   * Applying it to the other four actions as well is deliberate: resolving,
   * archiving, restoring and re-opening all change whether a record accepts
   * acknowledgement requests, so the acknowledgement views are stale after those
   * too. The helper is a superset of the event-record one, so nothing is lost.
   */
  const runAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
    fallback: string,
  ) => {
    try {
      await action();
      toast.success(successMessage);
      await invalidateAcknowledgementQueries(queryClient);
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error, fallback));
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Events Hub
          </span>
          <span className="text-body font-body text-subtext-color">
            Fire drills, incidents and complaints recorded at your nursery.
          </span>
        </div>
      </div>

      <div className="w-full" data-testid="events-hub-tabs">
        <Tabs>
          <Tabs.Item
            active={tab === "open"}
            icon={<FeatherAlertCircle />}
            onClick={() => setTab("open")}
          >
            Open
            <CountBadge count={counts.open} />
          </Tabs.Item>
          <Tabs.Item
            active={tab === "awaiting-acknowledgement"}
            icon={<FeatherPenTool />}
            onClick={() => setTab("awaiting-acknowledgement")}
          >
            Awaiting acknowledgement
            <CountBadge count={counts.awaitingAcknowledgement} />
          </Tabs.Item>
          <Tabs.Item
            active={tab === "history"}
            icon={<FeatherHistory />}
            onClick={() => setTab("history")}
          >
            History
            <CountBadge count={counts.history} />
          </Tabs.Item>
        </Tabs>
      </div>

      <div className="flex w-full flex-col items-start gap-4 pb-8">
        <EventsHubFilters
          categories={allCategories}
          categoryId={categoryId}
          onCategoryChange={(next) => {
            setCategoryId(next);
            setPage(0);
          }}
          eventTypes={eventTypes}
          eventTypeId={eventTypeId}
          onEventTypeChange={(next) => {
            setEventTypeId(next);
            setPage(0);
          }}
          nurseries={nurseries}
          selectedNurseryId={selectedNurseryId}
          onNurseryChange={(nurseryId) => {
            const nursery = nurseries.find((n) => n.id === nurseryId);
            if (nursery) {
              setSelectedNursery(nursery.id, nursery.organisationId);
              // Event types are per-nursery, so a held id belongs to the nursery
              // being left; keeping it would filter everything out with no visible
              // cause. Categories are global, so they survive.
              setEventTypeId(undefined);
              setPage(0);
            }
          }}
          canReset={hasActiveFilters({ categoryId, eventTypeId })}
          onReset={() => {
            setCategoryId(EMPTY_FILTERS.categoryId);
            setEventTypeId(EMPTY_FILTERS.eventTypeId);
            setPage(0);
          }}
        />

        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : isError ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-error-600">
              We couldn&apos;t load your events. Please try again.
            </span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-subtext-color">
              {EMPTY_MESSAGE[tab]}
            </span>
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <EventsHubTable
                items={items}
                activeColumn={effectiveSort.column}
                activeDir={effectiveSort.dir}
                onSort={handleSort}
                onView={(id) => navigate({ to: `/event-records/${id}` })}
                renderRowMenu={(record) => {
                  // Every item below is gated on a server-resolved capability
                  // (NAU-342). Each flag already combines the nursery's configured
                  // roles with what this record's status permits, so there are no
                  // status checks left here to drift from the API's own rules.
                  // Defaulted rather than dereferenced: the field is always present
                  // from this API, but a web build running ahead of core-api during a
                  // rolling deploy would otherwise throw inside render and blank the
                  // whole table instead of just hiding a menu.
                  const can =
                    record.capabilities ?? NO_EVENT_RECORD_CAPABILITIES;

                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <IconButton
                          icon={<FeatherMoreVertical />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onClick={() =>
                            navigate({ to: `/event-records/${record.id}` })
                          }
                        >
                          View
                        </DropdownMenuItem>

                        {can.canAcknowledge && (
                          <DropdownMenuItem
                            onClick={() =>
                              runAction(
                                () =>
                                  acknowledge.mutateAsync({
                                    eventRecordId: record.id,
                                  }),
                                "Event acknowledged",
                                "Failed to acknowledge the event",
                              )
                            }
                          >
                            Acknowledge
                          </DropdownMenuItem>
                        )}

                        {can.canResolve && (
                          <DropdownMenuItem
                            onClick={() =>
                              runAction(
                                () =>
                                  resolve.mutateAsync({
                                    eventRecordId: record.id,
                                  }),
                                "Event marked resolved",
                                "Failed to resolve the event",
                              )
                            }
                          >
                            Mark as resolved
                          </DropdownMenuItem>
                        )}

                        {can.canArchive && (
                          <DropdownMenuItem
                            onClick={() =>
                              runAction(
                                () =>
                                  archive.mutateAsync({
                                    eventRecordId: record.id,
                                  }),
                                "Event archived",
                                "Failed to archive the event",
                              )
                            }
                          >
                            Archive
                          </DropdownMenuItem>
                        )}

                        {can.canRestore && (
                          <DropdownMenuItem
                            onClick={() =>
                              runAction(
                                () =>
                                  restore.mutateAsync({
                                    eventRecordId: record.id,
                                  }),
                                "Event restored from archive",
                                "Failed to restore the event",
                              )
                            }
                          >
                            Restore from archive
                          </DropdownMenuItem>
                        )}

                        {can.canReopen && (
                          <DropdownMenuItem
                            onClick={() =>
                              runAction(
                                () =>
                                  reopen.mutateAsync({
                                    eventRecordId: record.id,
                                  }),
                                "Event re-opened",
                                "Failed to re-open the event",
                              )
                            }
                          >
                            Re-open
                          </DropdownMenuItem>
                        )}

                        {can.canDelete && (
                          <DropdownMenuItem
                            onClick={() => setPendingDelete(record)}
                          >
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }}
              />
            </TableScrollContainer>

            <Pagination
              page={page + 1}
              pageSize={pageSize}
              totalCount={total}
              onPageChange={(next) => setPage(next - 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              itemNoun={itemNounFor(tab)}
            />
          </>
        )}
      </div>

      {pendingDelete && (
        <DeleteEventRecordDialog
          open={!!pendingDelete}
          onOpenChange={(open) => {
            if (!open) setPendingDelete(null);
          }}
          record={pendingDelete}
          onDeleted={() => {
            void invalidateEventRecordQueries(queryClient);
          }}
        />
      )}
    </div>
  );
}

export default EventsHubPage;
